import {
  Button,
  InfoTipHint,
  Input,
  Select,
  type SelectItem,
} from "@cynthai/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { useCurrentOrganization } from "@/core/hooks/use-current-organization";
import { apiErrorData, isApiError } from "@/core/lib/api-errors";
import { addToast } from "@/core/lib/toast";
import { LedgerSourceQueryFields } from "@/features/classification/components/template-form/LedgerSourceQueryEditor";
import { StatementTreeCsvImportButton } from "@/features/classification/components/template-form/StatementTreeCsvImportButton";
import { StatementTreeLayout } from "@/features/classification/components/template-form/StatementTreeLayout";
import { StatementTreeRowsEditor } from "@/features/classification/components/template-form/StatementTreeRowsEditor";
import { useDraftRows } from "@/features/classification/hooks/use-draft-rows";
import { ledgerQueryReasonMessage } from "@/features/classification/lib/ledger-error-message";
import {
  sourceQuerySchema,
  toLedgerQuery,
} from "@/features/classification/lib/ledger-source-query";
import {
  type DraftRow,
  duplicateRowsDescription,
  publishIssueMessages,
  toDraftRows,
  toWriteRows,
} from "@/features/classification/lib/statement-tree";
import type { CreateLedgerClassificationTemplateError } from "@/openapi";
import {
  createLedgerClassificationTemplateMutation,
  listStatementTaxonomiesOptions,
  listStatementTaxonomiesQueryKey,
  readDefaultStatementTaxonomyOptions,
  readStatementTaxonomyOptions,
} from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

const createTreeSchema = sourceQuerySchema.extend({
  name: z.string().trim().min(1, m.field_is_required()),
});
type CreateTreeValues = z.infer<typeof createTreeSchema>;

// neither the empty start nor the packaged default has an id of its own
const EMPTY_SOURCE = "__empty__";
const DEFAULT_SOURCE = "__default__";
const CSV_SOURCE = "__csv__";

export function CreateAuditStatementTreeForm() {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const client = useQueryClient();
  const navigate = useNavigate();
  const draft = useDraftRows();
  const [sourceId, setSourceId] = useState(EMPTY_SOURCE);
  const latestSourceId = useRef(sourceId);
  const library = useQuery(
    listStatementTaxonomiesOptions({
      path: { org_id: organizationId },
      query: { project_type: "audit" },
    }),
  );
  const form = useForm<CreateTreeValues>({
    resolver: zodResolver(createTreeSchema),
    defaultValues: {
      name: "",
      origin_id: "",
      sql_query: "",
      identifiers: "",
      columns: "",
    },
  });
  const create = useMutation({
    ...createLedgerClassificationTemplateMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: listStatementTaxonomiesQueryKey({
          path: { org_id: organizationId },
        }),
      }),
  });

  const sourceItems: SelectItem<string>[] = [
    { label: m.statement_tree_create_source_empty(), value: EMPTY_SOURCE },
    ...(sourceId === CSV_SOURCE
      ? [
          {
            label: m.statement_tree_create_source_csv(),
            value: CSV_SOURCE,
            disabled: true,
          },
        ]
      : []),
    { label: m.statement_taxonomy_status_default(), value: DEFAULT_SOURCE },
    ...(library.data ?? []).flatMap((tree) =>
      tree.id === null ? [] : [{ label: tree.name, value: tree.id }],
    ),
  ];

  const startFrom = async (nextSourceId: string) => {
    setSourceId(nextSourceId);
    latestSourceId.current = nextSourceId;
    if (nextSourceId === EMPTY_SOURCE) {
      draft.setRows([]);
      return;
    }
    const path = { org_id: organizationId };
    try {
      const tree = await (nextSourceId === DEFAULT_SOURCE
        ? client.query(readDefaultStatementTaxonomyOptions({ path }))
        : client.query(
            readStatementTaxonomyOptions({
              path: { ...path, taxonomy_id: nextSourceId },
            }),
          ));
      // a slower fetch for an earlier pick must not overwrite the current one
      if (latestSourceId.current === nextSourceId) {
        draft.setRows(toDraftRows(tree.rows));
      }
    } catch (error) {
      addToast({ title: m.error_occurred(), severity: "danger", error });
    }
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({
        path: { org_id: organizationId },
        body: {
          name: values.name,
          rows: toWriteRows(draft.rows),
          query: toLedgerQuery(values),
        },
      });
      addToast({ title: m.statement_tree_created(), severity: "success" });
      navigate({
        to: "/template-editor/statement-trees/$taxonomyId",
        params: { taxonomyId: created.id },
      });
    } catch (error) {
      const failure = isApiError<CreateLedgerClassificationTemplateError>(error)
        ? error
        : undefined;
      if (failure && isApiError(failure, "STATEMENT_TAXONOMY_NAME_TAKEN")) {
        form.setError("name", { message: m.statement_taxonomy_name_taken() });
        return;
      }
      if (failure && isApiError(failure, "STATEMENT_TAXONOMY_DUPLICATE_ROWS")) {
        addToast({
          title: m.statement_tree_duplicate_rows(),
          description: m.statement_tree_duplicate_rows_description_$paths({
            paths: duplicateRowsDescription(
              draft.rows,
              apiErrorData(failure).duplicate_row_indexes,
            ),
          }),
          severity: "danger",
          error,
        });
        return;
      }
      const issues =
        failure && isApiError(failure, "STATEMENT_TAXONOMY_NOT_PUBLISHABLE")
          ? publishIssueMessages(apiErrorData(failure).issues)
          : [];
      const title =
        failure && isApiError(failure, "INVALID_LEDGER_QUERY")
          ? ledgerQueryReasonMessage(apiErrorData(failure).reason)
          : issues[0];
      addToast({
        title: title ?? m.error_occurred(),
        description: issues.slice(1).join(" · ") || undefined,
        severity: "danger",
        error,
      });
    }
  });

  const importRows = (rows: DraftRow[]) => {
    latestSourceId.current = CSV_SOURCE;
    setSourceId(CSV_SOURCE);
    draft.setRows(rows);
  };

  return (
    <FormProvider {...form}>
      <StatementTreeLayout
        identity={
          <>
            <Input
              isRequired
              size="custom"
              variant="bordered"
              className="max-w-xl flex-1"
              label={m.statement_taxonomy_create_name()}
              isInvalid={!!form.formState.errors.name}
              errorMessage={form.formState.errors.name?.message}
              {...form.register("name")}
            />
            <Select
              label={m.statement_taxonomy_create_source()}
              items={sourceItems}
              value={sourceId}
              onChange={startFrom}
              isDisabled={create.isPending}
            />
          </>
        }
        actions={
          <div className="flex gap-2">
            <StatementTreeCsvImportButton
              isDisabled={create.isPending}
              onImport={importRows}
            />
            <Button
              variant="default"
              color="primary"
              isLoading={create.isPending}
              onPress={() => submit()}
            >
              {m.statement_tree_create_submit()}
            </Button>
          </div>
        }
        notice={
          <div className="space-y-2">
            <p className="text-text-tertiary text-sm">
              {m.statement_tree_create_description()}
            </p>
            <InfoTipHint
              label={m.statement_tree_csv_import_format()}
              className="flex-col items-start gap-1.5"
            >
              <span className="block max-w-4xl leading-relaxed text-pretty">
                {m.statement_tree_csv_import_hint()}
              </span>
            </InfoTipHint>
          </div>
        }
        sourceQuery={<LedgerSourceQueryFields isDisabled={create.isPending} />}
        rows={
          <StatementTreeRowsEditor
            rows={draft.rows}
            isReadOnly={false}
            onAdd={draft.add}
            onUpdate={draft.update}
            onRemove={draft.remove}
            onReorder={draft.reorder}
          />
        }
      />
    </FormProvider>
  );
}
